import { useState, useEffect, useMemo } from "react";
import {
  Grid3X3,
  Search,
  RefreshCw,
  Users,
  KeyRound,
  Shield,
} from "lucide-react";
import { useAccessMatrix } from "@/hooks/use-access-matrix";
import { RoleBadge } from "@/components/shared/RoleBadge";
import { PageHeader } from "@/components/shared/page-header";
import { EmptyState } from "@/components/shared/empty-state";
import { Avatar, AvatarFallback, Button, Card, CardContent, Input, ScrollArea, Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@fox1ck-org/ui";

const PAGE_SIZE = 30;

export default function AccessMatrix() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setPage(0), [debouncedSearch]);

  const { data, isLoading, refetch } = useAccessMatrix({
    search: debouncedSearch || undefined,
    first: page * PAGE_SIZE,
    max: PAGE_SIZE,
  });

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const apps = data?.apps ?? [];
  const total = data?.total ?? 0;

  // Summary stats
  const usersWithAccess = useMemo(
    () => rows.filter((r) => Object.keys(r.access).length > 0).length,
    [rows]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Матрица доступа"
        description="Доступ пользователей к приложениям через участие в подразделениях"
      >
        <Button variant="ghost" size="icon" onClick={() => refetch()}>
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
        </Button>
      </PageHeader>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4">
        <SummaryCard
          icon={<Users className="h-4 w-4 text-primary" />}
          label="Пользователи (на странице)"
          value={rows.length}
        />
        <SummaryCard
          icon={<Users className="h-4 w-4 text-blue-400" />}
          label="Всего пользователей"
          value={total}
        />
        <SummaryCard
          icon={<KeyRound className="h-4 w-4 text-purple-400" />}
          label="Приложения"
          value={apps.length}
        />
        <SummaryCard
          icon={<Shield className="h-4 w-4 text-green-400" />}
          label="С доступом"
          value={usersWithAccess}
        />
      </div>

      {/* Search */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Поиск по имени, почте или логину…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground">
          {rows.length} of {total} user{total !== 1 ? "s" : ""}
          {apps.length > 0 && ` · приложений: ${apps.length}`}
        </span>
      </div>

      {/* Matrix */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Grid3X3 className="h-10 w-10" />}
          title={debouncedSearch ? "Никого не нашлось" : "Пользователей нет"}
          description="Здесь появятся пользователи и их доступ к приложениям."
        />
      ) : apps.length === 0 ? (
        <EmptyState
          icon={<KeyRound className="h-10 w-10" />}
          title="AppRoleBinding нет"
          description="Разверните CRD AppRoleBinding в кластере, чтобы увидеть матрицу."
        />
      ) : (
        <ScrollArea className="w-full">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[220px] sticky left-0 bg-background z-10">
                    Пользователь
                  </TableHead>
                  {apps.map((app) => (
                    <TableHead key={app} className="min-w-[140px] text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate max-w-[120px]">{app}</span>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const displayName =
                    row.user.firstName || row.user.lastName
                      ? `${row.user.firstName ?? ""} ${row.user.lastName ?? ""}`.trim()
                      : row.user.username;
                  const initial = (
                    row.user.firstName?.[0] ?? row.user.username[0] ?? "?"
                  ).toUpperCase();

                  return (
                    <TableRow key={row.user.id}>
                      <TableCell className="sticky left-0 bg-background z-10">
                        <div className="flex items-center gap-2.5">
                          <Avatar className="h-7 w-7">
                            <AvatarFallback className="text-xs">{initial}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{displayName}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {row.user.email ?? `@${row.user.username}`}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      {apps.map((app) => {
                        const appAccess = row.access[app];
                        return (
                          <TableCell key={app} className="text-center">
                            {appAccess ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex flex-wrap justify-center gap-1">
                                      {appAccess.roles.map((role) => (
                                        <RoleBadge key={role} name={role} />
                                      ))}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">
                                      Через: {appAccess.orgUnitTypes.join(", ")}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <span className="text-muted-foreground/40">-</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            Назад
          </Button>
          <span className="text-sm text-muted-foreground">
            Страница {page + 1} из {Math.ceil(total / PAGE_SIZE)}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={(page + 1) * PAGE_SIZE >= total}
          >
            Вперёд
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <div className="flex items-center justify-center w-8 h-8 rounded-md bg-muted">
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
